import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { Settings } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useB2BMode } from '../../hooks/useB2BMode';
import { getMyTeam } from '../../services/api';
import AppSidebar from '../AppSidebar';
import { ReactComponent as SidebarHomeIcon } from '../../assets/icons/sidebar/ico_sidebar_home.svg';
import { ReactComponent as SidebarPersonaIcon } from '../../assets/icons/sidebar/ico_sidebar_persona.svg';
import { ReactComponent as SidebarResearchIcon } from '../../assets/icons/sidebar/ico_sidebar_research.svg';
import { ReactComponent as SidebarTestIcon } from '../../assets/icons/sidebar/ico_sidebar_test.svg';

const B2B_RESEARCH_PATH = '/research';

const B2BSidebar = ({
  isCollapsed,
  onToggleCollapse,
  hideToggleButton = false,
  navigationGuard,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser, isEnterprise } = useAppContext();
  const { enableB2BMode, disableB2BMode } = useB2BMode();
  const [isSettingsMode, setIsSettingsMode] = useState(location.pathname.startsWith('/b2b/settings'));
  const [isSettingsOwner, setIsSettingsOwner] = useState(
    user?.company_role === 'owner' || user?.role === 'owner'
  );

  useEffect(() => {
    setIsSettingsMode(location.pathname.startsWith('/b2b/settings'));
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const loadMyRole = async () => {
      if (!user?.id || user?.account_type !== 'business') {
        setIsSettingsOwner(false);
        return;
      }

      if (user?.company_role === 'owner' || user?.role === 'owner') {
        setIsSettingsOwner(true);
        return;
      }

      try {
        const data = await getMyTeam();
        if (cancelled) return;
        const currentMember = (data.members || []).find((member) => {
          const memberId = member?.user_id || member?.id || member?.user?.id;
          return String(memberId) === String(user.id);
        });
        setIsSettingsOwner((currentMember?.role || currentMember?.company_role) === 'owner');
      } catch (err) {
        if (!cancelled) {
          setIsSettingsOwner(false);
        }
      }
    };

    loadMyRole();

    return () => {
      cancelled = true;
    };
  }, [user?.account_type, user?.company_role, user?.id, user?.role]);

  const isActive = (path) => {
    const is2DepthOrMore =
      location.pathname.startsWith('/project/') ||
      location.pathname.startsWith('/study/');

    if (is2DepthOrMore) return false;
    if (path === '/b2b') return location.pathname === '/b2b' || location.pathname === '/b2b/home';
    if (path === B2B_RESEARCH_PATH) {
      return location.pathname === B2B_RESEARCH_PATH;
    }
    if (path === '/personas/explore') {
      return location.pathname === '/personas/explore' || location.pathname.startsWith('/personas/explore/');
    }
    if (path === '/personas') {
      return (
        (location.pathname === '/personas' || location.pathname.startsWith('/personas/'))
        && !location.pathname.startsWith('/personas/explore')
      );
    }
    if (path === '/personas-root') {
      return location.pathname === '/personas'
        || location.pathname.startsWith('/personas/');
    }
    if (path === '/tests') return location.pathname === '/tests' || location.pathname.startsWith('/tests/');
    return false;
  };

  const closeSettingsMode = () => {
    setIsSettingsMode(false);
  };

  const canNavigate = (targetPath, proceed) => (
    typeof navigationGuard === 'function' ? navigationGuard(targetPath, proceed) : true
  );

  const openSettingsMode = () => {
    const targetPath = '/b2b/settings?section=account';
    const proceed = () => {
      setIsSettingsMode(true);
      navigate(targetPath);
    };
    if (canNavigate(targetPath, proceed)) proceed();
  };

  const handleLogout = () => {
    const loginPath = isEnterprise ? '/login' : '/individual/login';
    const proceed = () => {
      logoutUser();
      disableB2BMode();
      navigate(loginPath);
    };
    if (canNavigate(loginPath, proceed)) proceed();
  };

  const navItems = [
    {
      path: '/b2b',
      label: 'Home',
      key: 'home',
      sidebarIcon: SidebarHomeIcon,
      active: isActive('/b2b'),
      onClick: (event) => {
        const proceed = () => {
          if (isSettingsMode) closeSettingsMode();
          navigate('/b2b');
        };
        if (!canNavigate('/b2b', proceed)) {
          event?.preventDefault();
          return;
        }
        if (isSettingsMode) closeSettingsMode();
      },
    },
    {
      path: B2B_RESEARCH_PATH,
      label: 'Research',
      key: 'research',
      sidebarIcon: SidebarResearchIcon,
      active: isActive(B2B_RESEARCH_PATH),
      onClick: (event) => {
        const proceed = () => {
          if (isSettingsMode) closeSettingsMode();
          enableB2BMode();
          navigate(B2B_RESEARCH_PATH);
        };
        if (!canNavigate(B2B_RESEARCH_PATH, proceed)) {
          event?.preventDefault();
          return;
        }
        if (isSettingsMode) closeSettingsMode();
        enableB2BMode();
      },
    },
    isEnterprise
      ? {
          // Enterprise keeps the original flat Persona entry → Generate list.
          path: '/personas',
          label: 'Persona',
          key: 'persona',
          sidebarIcon: SidebarPersonaIcon,
          active: isActive('/personas'),
          onClick: (event) => {
            const proceed = () => {
              if (isSettingsMode) closeSettingsMode();
              navigate('/personas');
            };
            if (!canNavigate('/personas', proceed)) {
              event?.preventDefault();
              return;
            }
            if (isSettingsMode) closeSettingsMode();
          },
        }
      : {
          // Individual: Explore + Generate under Persona; default landing is Explore.
          path: '/personas/explore',
          label: 'Persona',
          key: 'persona',
          sidebarIcon: SidebarPersonaIcon,
          active: isActive('/personas-root'),
          onClick: (event) => {
            const proceed = () => {
              if (isSettingsMode) closeSettingsMode();
              navigate('/personas/explore');
            };
            if (!canNavigate('/personas/explore', proceed)) {
              event?.preventDefault();
              return;
            }
            if (isSettingsMode) closeSettingsMode();
          },
          children: [
            {
              path: '/personas/explore',
              label: 'Explore',
              key: 'persona-explore',
              active: isActive('/personas/explore'),
              onClick: (event) => {
                const proceed = () => {
                  if (isSettingsMode) closeSettingsMode();
                  navigate('/personas/explore');
                };
                if (!canNavigate('/personas/explore', proceed)) {
                  event?.preventDefault();
                  return;
                }
                if (isSettingsMode) closeSettingsMode();
              },
            },
            {
              path: '/personas',
              label: 'Generate',
              key: 'persona-generate',
              active: isActive('/personas'),
              onClick: (event) => {
                const proceed = () => {
                  if (isSettingsMode) closeSettingsMode();
                  navigate('/personas');
                };
                if (!canNavigate('/personas', proceed)) {
                  event?.preventDefault();
                  return;
                }
                if (isSettingsMode) closeSettingsMode();
              },
            },
          ],
        },
    {
      path: '/tests',
      label: 'Test',
      key: 'test',
      sidebarIcon: SidebarTestIcon,
      active: isActive('/tests'),
      onClick: (event) => {
        const proceed = () => {
          if (isSettingsMode) closeSettingsMode();
          navigate('/tests');
        };
        if (!canNavigate('/tests', proceed)) {
          event?.preventDefault();
          return;
        }
        if (isSettingsMode) closeSettingsMode();
      },
    },
  ];

  const settingsItems = [
    { key: 'account', label: '계정설정', path: '/b2b/settings?section=account' },
    { key: 'figma', label: '피그마 연동', path: '/b2b/settings/figma' },
    { key: 'members', label: '멤버 관리', path: '/b2b/settings?section=members' },
    { key: 'membership', label: '멤버십 관리', path: '/b2b/settings?section=membership' },
  ].filter((item) => {
    if (!isEnterprise && ['members', 'membership'].includes(item.key)) return false;
    return !['members', 'membership'].includes(item.key) || isSettingsOwner;
  })
    .map((item) => ({
      ...item,
      onClick: (event) => {
        const proceed = () => navigate(item.path);
        if (!canNavigate(item.path, proceed)) {
          event?.preventDefault();
        }
      },
    }));

  const isSettingsActive = (item) => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section') || 'account';
    if (item.key === 'figma') return location.pathname === '/b2b/settings/figma';
    if (location.pathname === '/b2b/settings/figma') return false;
    return location.pathname.startsWith('/b2b/settings') && section === item.key;
  };

  return (
    <AppSidebar
      variant="b2b"
      isCollapsed={isCollapsed}
      settingsMode={isSettingsMode}
      onToggleCollapse={onToggleCollapse}
      onLogoClick={() => {
        if (onToggleCollapse) onToggleCollapse();
      }}
      hideToggleButton={hideToggleButton}
      navItems={navItems}
      settingsAction={{
        label: 'Setting',
        title: '설정',
        lucideIcon: Settings,
        active: isSettingsMode,
        onClick: openSettingsMode,
      }}
      settingsPanelTitle="설정"
      settingsItems={settingsItems}
      isSettingsItemActive={isSettingsActive}
      userName={user?.name || '사용자'}
      userEmail={user?.email || 'user@example.com'}
      profileMenuItems={[
        {
          key: 'logout',
          label: '로그아웃',
          icon: faSignOutAlt,
          danger: true,
          onClick: handleLogout,
        },
      ]}
    />
  );
};

export default B2BSidebar;
